import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import SubscriptionBirthCohabitationPage from 'subscription/pages/registration-birth-family';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ActCategory } from 'shell/models/act-category-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { transformFamilyDetail } from 'notifications-from-civil-status/utils/transform-family-detail';
import { SubscriptionBirthFamilyFormVM } from 'subscription/view-models/subscription-registration-birth-family';
import { useEventCallback } from '@mui/material';
import { IdStateNotification } from 'notifications-from-civil-status/constants/id-state-notification';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import ErrorModal from 'shell/components/shared/modal-error';

interface ResearchFamilyNotificationsDetailSharedProps {
  goBackUrl?: string;
  goBackUrlDetail?: string;
  items?: BreadcrumbItem[];
  isOther?: boolean;
  isMother?: boolean;
  isFather?: boolean;
  isFromCivilStatus?: boolean;
}

const ResearchFamilyNotificationsDetailShared = ({
  goBackUrl = '',
  goBackUrlDetail = '',
  items = [],
  isFather,
  isMother,
  isOther,
  isFromCivilStatus,
}: ResearchFamilyNotificationsDetailSharedProps): ReactElement => {
  const [modalError, setModalError] = useState<string>('');
  const params = useParams();

  const idNotifica = useMemo(() => {
    return params.idNotifica || '';
  }, [params.idNotifica]);

  const idAnsc = useMemo(() => {
    return params.id || undefined;
  }, [params.id]);

  const [dataEvent, getDataEvent] = useHttpFetch<
    HttpResponse<ActCategory>,
    SubscriptionBirthFamilyFormVM
  >(`${__ACT_API_URL__}/consultazione/evento`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (res) => {
      const castedRes = transformFamilyDetail(res, false, isOther, isMother, isFather);

      if (castedRes.error) {
        setModalError(castedRes.error);
      }

      return castedRes.data as SubscriptionBirthFamilyFormVM;
    },
  });

  useEffect(() => {
    if (idAnsc) {
      getDataEvent({
        data: {
          criteriRicercaEvento: {
            idAnsc,
          },
        },
      });
    }
  }, [idAnsc]);

  const saveData = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const navigate = useNavigate();

  const onConfirm = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.RegistrazioneEffettuata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrlDetail);
    });
  });

  const onHandleExit = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.DaRegistrare,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrl);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={items} />
      {dataEvent.data && !dataEvent.pristine && !dataEvent.isLoading && !dataEvent.hasErrors ? (
        <SubscriptionBirthCohabitationPage
          isWithoutBreadcrumbs
          goBackUrl={goBackUrl} // torna alla lista di ricerca
          initialValues={(dataEvent.data || {}) as SubscriptionBirthFamilyFormVM}
          onConfirm={onConfirm}
          onHandleExit={onHandleExit}
          disabledFromAct
          isFromCivilStatus={isFromCivilStatus}
        />
      ) : undefined}
      {Boolean(modalError) ? (
        <ErrorModal
          message={modalError}
          onClose={() => {
            setModalError('');
          }}
          open={Boolean(modalError)}
        />
      ) : undefined}
    </Fragment>
  );
};

ResearchFamilyNotificationsDetailShared.displayName =
  'Research Family Notifications Page component';

export default ResearchFamilyNotificationsDetailShared;
